import React, { useEffect, useMemo, useRef, useState } from 'react';

import { commodityApis, dashboardCommodityApis } from '@apis/next/commodityApis';
import defaultPhoto from '@assets/img/arts/default-photo.png';
import addSquareIcon from '@assets/img/icons/add-square.svg';
import pdfIcon from '@assets/img/icons/pdf.svg';
import refreshIcon from '@assets/img/icons/refresh.svg';
import subtractSquareIcon from '@assets/img/icons/subtract-square.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IconButton } from '@components/IconButton';
import { QueryContainer } from '@components/QueryContainer';
import { CommoditySearch } from '@components/utilities/Commodity/CommoditySearch';
import { useSearch } from '@hooks/next/useSearch';
import { Commodity } from '@models/Commodity';
import { CommoditySearchQuery } from '@models/next/Commodity';
import { getNextPageParam } from '@models/next/PaginationType';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { isImageFileName, isPdfFileName } from '@utils/types';
import clsx from 'clsx';
import useIntersection from 'react-use/lib/useIntersection';

const strings = createStrings({
  ja: {
    chooseCommoditiesPrompt: '商品を選択してください',
    canSelectMultiple: '＊複数の商品を選択するのは可能です。',
    addCommodity: '新しい商品を追加',
    editOrDeleteCommodities: '商品を編集または削除',
    noData: (onClick: () => void, addCommodity?: boolean) => (
      <>
        まだ商品を登録していません。
        <br />
        {addCommodity && (
          <span>
            「<a onClick={onClick}>新しい商品を追加</a>
            」をクリックして商品を登録してください。
          </span>
        )}
      </>
    ),
    noResult: () => <>お探しの商品は見つかりません。ほかの条件で検索してください。</>,
    askCustomerToEditCommodity: () => (
      <>
        *CSはお客さんの商品情報を変更または追加することはできません。
        <br />
        お客さんの製品情報が不足している場合は、お客様に連絡して、お客様自身の製品リストを改善するよう依頼してください。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem 1rem',
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    paddingTop: '1rem',
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  contentRoot: {
    paddingTop: '1rem',
    paddingBottom: '0.5rem',
  },
  commodityList: {
    ...common.flexInline,
    flexWrap: 'wrap',
    height: 360,
    overflow: 'auto',
    padding: '5px 17px',
    rowGap: 24,
    columnGap: 15,
  },
  commodityContainer: {
    ...common.flexColumn,
    ...common.middleText,
    ...common.textGray500,
    height: 233,
    width: 214,
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    overflow: 'hidden',
    '&$selected': {
      borderColor: theme.palette.primary.main,
    },
  },
  commodityPhoto: {
    ...common.flexColumnCenter,
    rowGap: '1rem',
    textAlign: 'center',
    flex: 1,
    minHeight: 0,
    objectFit: 'cover',
    marginTop: 3,
    marginLeft: 7,
    marginRight: 7,
  },
  commodityField: {
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    lineHeight: '25px',
    backgroundColor: theme.palette.grey[50],
  },
  commodityName: {},
  commodityNo: {
    ...common.textGray600,
    '$selected &': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
    },
    '$disabled &': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.grey[400],
    },
  },
  commodityNoImage: {
    ...common.flexCenter,
    flex: 1,
    background: theme.palette.grey[200],
  },
  noData: {
    flex: 1,
    ...common.textGray500,
    ...common.smallerText,
    ...common.flexColumnCenter,
    '& a': {
      ...common.caution,
      ...common.baseButton,
      textDecoration: 'underline',
    },
  },
  prompt: {
    ...common.smallerText,
    color: theme.palette.error.main,
    padding: '10px 20px',
  },
  selected: {},
  disabled: {},
}));

interface CommodityItemProps {
  item: Commodity;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

const CommodityItem = React.forwardRef<HTMLDivElement, CommodityItemProps>((props, ref) => {
  const { item, selected, disabled, onSelect } = props;

  const image = item.images[0] ?? defaultPhoto;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isImage = image.url && isImageFileName(image.url);
  const isPdf = image.url && isPdfFileName(image.url);

  return (
    <div
      ref={ref}
      className={clsx(
        classes.commodityContainer,
        !disabled && classes.baseButton,
        selected && classes.selected,
        disabled && classes.disabled,
      )}
      onClick={disabled ? undefined : onSelect}
    >
      {
        <>
          {isImage ? (
            <img className={clsx(classes.commodityPhoto)} src={image.url} />
          ) : isPdf ? (
            <div className={clsx(classes.commodityPhoto)}>
              <img src={pdfIcon} />
              {image.name}
            </div>
          ) : (
            <div className={clsx(classes.commodityNoImage)}>
              <img src={defaultPhoto} />
            </div>
          )}
        </>
      }

      <div className={clsx(classes.commodityName, classes.commodityField)}>
        {item.name_en || item.name_ja || '-'}
      </div>
      <div style={{ height: 1 }} />
      <div className={clsx(classes.commodityNo, classes.commodityField)}>
        {item.commodity_no || '-'}
      </div>
    </div>
  );
});

export interface CommodityFilterProps {
  open: boolean;
  onClose: (values: number[]) => void;
  disabledIds: number[];
  user_id?: number;
  isAdmin?: boolean;
}

export const CommodityFilter: React.FC<CommodityFilterProps> = (props) => {
  const { open, onClose, disabledIds, isAdmin = false, user_id } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(CommoditySearchQuery);

  const commoditiesQuery = !isAdmin
    ? commodityApis.useInfiniteQuery(
        '/commodity',
        {
          queries: {
            ...search,
            page: 1,
            page_size: 10,
          },
        },
        {
          getPageParamList: () => ['page'],
          getNextPageParam,
        },
      )
    : dashboardCommodityApis.useInfiniteQuery(
        '/dashboard/commodity',
        {
          queries: {
            ...search,
            user_id,
            page: 1,
            page_size: 10,
          },
        },
        {
          getPageParamList: () => ['page'],
          getNextPageParam,
        },
      );

  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});

  const [, setDummy] = useState({});

  const lastRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const commodities = useMemo(
    () => commoditiesQuery.data?.pages.map((p) => p.results).flat() ?? [],
    [commoditiesQuery.data],
  );

  useEffect(() => {
    setDummy({});
  }, [commoditiesQuery.data]);

  const intersection = useIntersection(lastRef, {
    root: rootRef.current,
  });

  useEffect(() => {
    if (intersection?.isIntersecting && rootRef.current) {
      commoditiesQuery.fetchNextPage();
    }
  }, [intersection?.isIntersecting, rootRef.current]);

  const isQueryEmpty = !search.commodity_no && !search.name_en;

  const onAddCommodity = () => window.open(url('/utilities/Commodity/AddCommodity'), 'blank');

  const renderNoData = () => {
    return <div className={classes.noData}>{strings.noData(onAddCommodity, !isAdmin)}</div>;
  };

  const renderNoResult = () => {
    return <div className={classes.noData}>{strings.noResult()}</div>;
  };

  return (
    <ActionableDialog
      open={open}
      onClose={() => onClose(disabledIds)}
      size="lg"
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.chooseCommoditiesPrompt}</ActionableDialogTitle>
      <ActionableDialogContent classes={{ root: classes.contentRoot }}>
        <CommoditySearch />
        {!isAdmin && (
          <div className={classes.actionsContainer}>
            <IconButton
              icon={<img src={addSquareIcon} alt="Add Icon" />}
              text={strings.addCommodity}
              onClick={onAddCommodity}
            />
            <IconButton
              icon={<img src={subtractSquareIcon} alt="Minus Icon" />}
              text={strings.editOrDeleteCommodities}
              onClick={() => window.open(url('/utilities/Commodity/Commodities'), 'blank')}
            />
            <IconButton
              icon={<img src={refreshIcon} alt="Refresh Icon" />}
              text={formStrings.update}
              onClick={() => {
                commoditiesQuery.remove();
                commoditiesQuery.refetch();
              }}
            />
          </div>
        )}
      </ActionableDialogContent>

      <ActionableDialogContentDivider />

      {isAdmin && <div className={classes.prompt}>{strings.askCustomerToEditCommodity()}</div>}

      <QueryContainer style={{ height: 360 }} queries={[commoditiesQuery]}>
        <div className={classes.commodityList} ref={rootRef}>
          {commodities.map((commodity, i) => (
            <CommodityItem
              key={commodity.id}
              item={commodity}
              selected={selectedIds[commodity.id]}
              disabled={!!disabledIds.find((c) => c === commodity.id)}
              onSelect={() =>
                setSelectedIds({ ...selectedIds, [commodity.id]: !selectedIds[commodity.id] })
              }
              ref={i === commodities.length - 1 ? lastRef : undefined}
            />
          ))}
          {commodities.length === 0 && isQueryEmpty && renderNoData()}
          {commodities.length === 0 && !isQueryEmpty && renderNoResult()}
        </div>
      </QueryContainer>

      <ActionableDialogContentDivider />
      <div
        className={clsx(classes.flexInline, classes.justifyBetween, classes.itemsCenter)}
        style={{ padding: 16 }}
      >
        <div className={clsx(classes.smallerText, classes.textPrimary)} style={{ flex: 1 }}>
          {strings.canSelectMultiple}
        </div>
        <Button
          color="primary"
          text={formStrings.ok}
          size="small"
          onClick={() =>
            onClose([
              ...disabledIds,
              ...Object.entries(selectedIds)
                .filter(([, v]) => Boolean(v))
                .map(([k]) => Number(k)),
            ])
          }
          type="button"
        />
      </div>
    </ActionableDialog>
  );
};
